/** @jsx jsx */
import { jsx } from '@emotion/core';
import cmsUtils from '../../cms/utils/cmsUtils';
import { useEffect, useRef } from 'react';

export function Expr3ssJobWidget(props) {
  const item = props.item;
  const cssClass = props.cssClass;
  const code = cmsUtils.payload(item, 'Code') || '';
  const defaultWidgetId = cmsUtils.payload(item, 'WidgetId') || 'expr3ss-jobs-widget';
  const containerRef = useRef(null);
  
  // Extract widget ID from code or use default
  const divIdMatch = code.match(/id=['"]([^'"]+)['"]/);
  const widgetId = divIdMatch ? divIdMatch[1] : defaultWidgetId;

  // Helper functions to extract attributes from code
  const extractAttribute = (html, attrName) => {
    const regex = new RegExp(`data-${attrName}="([^"]+)"`, 'i');
    const match = html.match(regex);
    return match ? match[1] : null;
  };

  const extractArrayAttribute = (html, attrName) => {
    const value = extractAttribute(html, attrName);
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return value.split(',').map(v => v.trim());
    }
  };

  const extractBooleanAttribute = (html, attrName) => {
    const value = extractAttribute(html, attrName);
    if (value === null) return null;
    return value.toLowerCase() === 'true';
  };

  useEffect(() => {
    // Initialize Expr3ss if not already initialized (must be done first)
    if (typeof window.Expr3ss === 'undefined') {
      window.Expr3ss = [];
    }

    // Parse widget configuration from code
    let widgetConfig = null;

    // Try to extract the Expr3ss.Widget configuration from the code
    // Look for Expr3ss.Widget = {...} pattern in script tags
    // Handle cases where there's initialization code before the Widget assignment
    const scriptMatches = code.match(/<script[^>]*>([\s\S]*?)<\/script>/g);
    if (scriptMatches) {
      for (const scriptTag of scriptMatches) {
        const scriptContent = scriptTag.match(/<script[^>]*>([\s\S]*?)<\/script>/)[1];
        // Match Expr3ss.Widget = {...} even if there's code before it
        const configMatch = scriptContent.match(/Expr3ss\.Widget\s*=\s*({[\s\S]*?});/);
        if (configMatch) {
          try {
            // Use Function constructor to safely evaluate the object literal
            const func = new Function('return ' + configMatch[1]);
            widgetConfig = func();
            break;
          } catch (e) {
            console.error('Failed to parse Expr3ss widget configuration:', e);
            // Try JSON parsing as fallback
            try {
              let configStr = configMatch[1];
              // Handle unquoted keys in object literal
              configStr = configStr.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
              widgetConfig = JSON.parse(configStr);
              break;
            } catch (e2) {
              console.error('Failed to parse Expr3ss widget configuration (JSON fallback):', e2);
            }
          }
        }
      }
    }

    // If no config found, try to parse the entire code as JSON
    if (!widgetConfig) {
      try {
        widgetConfig = JSON.parse(code);
      } catch (e) {
        // Not JSON, continue with defaults
      }
    }

    // If still no config found, use defaults or extract from code attributes
    if (!widgetConfig) {
      widgetConfig = {
        config: {
          token: extractAttribute(code, 'token') || cmsUtils.payload(item, 'Token') || 'a28eb30008340d4633acd1f9a3db1f5f',
          parent: extractAttribute(code, 'parent') || cmsUtils.payload(item, 'Parent') || 'body',
          id: widgetId,
          sort: extractAttribute(code, 'sort') || cmsUtils.payload(item, 'Sort') || 'division:asc,title:asc',
          grouping: extractAttribute(code, 'grouping') || cmsUtils.payload(item, 'Grouping') || 'division',
          button: {
            label: extractArrayAttribute(code, 'buttonLabel') || ['Apply Now', 'Interest Only'],
            colour: extractArrayAttribute(code, 'buttonColour') || ['#004b91', '#433125', '#ffffff']
          },
          collapsed: extractBooleanAttribute(code, 'collapsed') ?? false,
          noDescription: extractBooleanAttribute(code, 'noDescription') ?? false,
          hideInterest: extractBooleanAttribute(code, 'hideInterest') ?? false,
          hideApply: extractBooleanAttribute(code, 'hideApply') ?? false,
          sourceTag: extractAttribute(code, 'sourceTag') || cmsUtils.payload(item, 'SourceTag') || '6'
        }
      };
    }

    // Ensure the config has the correct structure
    if (!widgetConfig.config) {
      widgetConfig = { config: widgetConfig };
    }

    // Update the widget ID in config to match the actual div ID
    if (widgetConfig.config) {
      widgetConfig.config.id = widgetId;
      // Keep the original parent value from config, or default to 'body'
      // The widget will look for the div with the specified ID in the parent
      if (!widgetConfig.config.parent || widgetConfig.config.parent === 'body') {
        widgetConfig.config.parent = 'body';
      }
    }

    // Set widget configuration BEFORE loading scripts
    // The widget script expects this to be set when it loads
    window.Expr3ss.Widget = widgetConfig;

    // Load jQuery if not already loaded
    const loadJQuery = () => {
      return new Promise((resolve) => {
        if (window.jQuery && window.jQuery.fn && window.jQuery.fn.jquery) {
          resolve();
          return;
        }

        const jqueryScript = document.createElement('script');
        jqueryScript.type = 'text/javascript';
        jqueryScript.src = 'https://ajax.googleapis.com/ajax/libs/jquery/3.7.1/jquery.min.js';
        jqueryScript.onload = () => resolve();
        jqueryScript.onerror = () => {
          console.error('Failed to load jQuery');
          resolve(); // Continue even if jQuery fails
        };
        document.head.appendChild(jqueryScript);
      });
    };

    // Load widget script
    const loadWidgetScript = () => {
      return new Promise((resolve) => {
        // Check if widget script is already loaded
        const existingScript = document.querySelector('script[src*="widget.min.js"]');
        if (existingScript) {
          // If script already exists, re-initialize
          setTimeout(() => {
            const targetDiv = document.getElementById(widgetId);
            if (!targetDiv) {
              console.warn(`Expr3ss widget target div #${widgetId} not found`);
            }
            
            if (window.jQuery && window.jQuery.fn && window.jQuery.fn.jquery) {
              if (window.Expr3ss && typeof window.Expr3ss.init === 'function') {
                window.Expr3ss.init();
              } else if (window.Expr3ss && window.Expr3ss.Widget) {
                console.log('Expr3ss widget config set (existing script):', window.Expr3ss.Widget);
              }
            }
          }, 200);
          resolve();
          return;
        }

        const widgetScript = document.createElement('script');
        widgetScript.type = 'text/javascript';
        widgetScript.src = 'https://developers.expr3ss.com/Files/js/widget.min.js';
        widgetScript.onload = () => {
          // Widget should auto-initialize, but ensure it happens
          // Wait for both jQuery and the widget div to be ready
          setTimeout(() => {
            // Verify the target div exists
            const targetDiv = document.getElementById(widgetId);
            if (!targetDiv) {
              console.warn(`Expr3ss widget target div #${widgetId} not found`);
            }
            
            // Check if jQuery is available
            if (window.jQuery && window.jQuery.fn && window.jQuery.fn.jquery) {
              // Try to initialize the widget
              if (window.Expr3ss && typeof window.Expr3ss.init === 'function') {
                window.Expr3ss.init();
              } else if (window.Expr3ss && window.Expr3ss.Widget) {
                // Widget might auto-initialize, but log for debugging
                console.log('Expr3ss widget config set:', window.Expr3ss.Widget);
              }
            } else {
              console.warn('jQuery not available when trying to initialize Expr3ss widget');
            }
          }, 200);
          resolve();
        };
        widgetScript.onerror = () => {
          console.error('Failed to load Expr3ss widget script');
          resolve(); // Continue even if script fails
        };
        document.head.appendChild(widgetScript);
      });
    };

    // Load scripts in sequence - config is already set above
    loadJQuery()
      .then(() => loadWidgetScript())
      .catch((error) => {
        console.error('Error loading Expr3ss widget scripts:', error);
      });

    // Cleanup function
    return () => {
      // Optional: cleanup if needed
    };
  }, [code, widgetId, item]);

  return (
    <div className={cssClass} id={item.anchorName} ref={containerRef}>
      <div id={widgetId}></div>
    </div>
  );
}

